import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { useStore, updateSettings, addCategory, updateCategory, deleteCategory, setQuotes, allCategories } from "@/lib/store";
import { toast } from "sonner";
import { CategoryTrash } from "@/components/CategoryTrash";

const PALETTE = ["#9BA3B5", "#CBA38D", "#D4B5B0", "#A3B899", "#D9C5B2", "#C48B97", "#E7CFD4", "#B9906E", "#A3B897", "#6A7368"];

export default function SettingsPage() {
  const store = useStore();
  const [newCatName, setNewCatName] = useState("");
  const [newCatColor, setNewCatColor] = useState(PALETTE[0]);
  const [quotesText, setQuotesText] = useState(store.settings.quotes.join("\n"));

  function saveQuotes() {
    const list = quotesText.split("\n").map((l) => l.trim()).filter(Boolean);
    setQuotes(list);
    toast.success("Frasi salvate");
  }

  return (
    <div className="max-w-3xl mx-auto flex flex-col gap-6" data-testid="settings-page">
      <h1 className="font-serif-display text-4xl text-[#3B443D]">Impostazioni</h1>

      {/* General */}
      <section className="tl-paper p-6">
        <h2 className="font-serif-display text-2xl text-[#3B443D] mb-4">Generali</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="tl-label block mb-1">Il tuo nome</label>
            <input
              data-testid="settings-name"
              value={store.settings.name}
              onChange={(e) => updateSettings({ name: e.target.value })}
              placeholder="Come vuoi essere chiamata"
              className="w-full px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm focus:outline-none focus:border-[#A3B897]"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="tl-label block mb-1">Griglia dalle (ora)</label>
              <input
                type="number" min={0} max={23}
                data-testid="settings-grid-start"
                value={store.settings.gridStart}
                onChange={(e) => updateSettings({ gridStart: Math.max(0, Math.min(23, Number(e.target.value) || 0)) })}
                className="w-full px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm focus:outline-none focus:border-[#A3B897]"
              />
            </div>
            <div>
              <label className="tl-label block mb-1">alle (ora)</label>
              <input
                type="number" min={1} max={24}
                data-testid="settings-grid-end"
                value={store.settings.gridEnd}
                onChange={(e) => updateSettings({ gridEnd: Math.max(1, Math.min(24, Number(e.target.value) || 24)) })}
                className="w-full px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm focus:outline-none focus:border-[#A3B897]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="tl-paper p-6">
        <h2 className="font-serif-display text-2xl text-[#3B443D] mb-4">Categorie</h2>
        <div className="flex flex-col gap-2">
          {allCategories(store).map((c) => (
            <div key={c.id} className="flex items-center gap-3" data-testid={`category-row-${c.id}`}>
              <ColorSwatch value={c.color} onChange={(v) => updateCategory(c.id, { color: v })} testId={`category-color-${c.id}`} />
              <input
                data-testid={`category-name-${c.id}`}
                value={c.name}
                onChange={(e) => updateCategory(c.id, { name: e.target.value })}
                className="flex-1 px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm focus:outline-none focus:border-[#A3B897]"
              />
              {c.orphan ? (
                <span className="w-4" aria-hidden="true" data-testid={`category-orphan-${c.id}`} />
              ) : (
              <button
                type="button"
                onClick={() => { if (window.confirm("Elimina questa categoria?")) deleteCategory(c.id); }}
                className="text-[#6A7368] hover:text-[#C48B97]"
                data-testid={`category-delete-${c.id}`}
                aria-label="Elimina categoria"
              >
                <Trash2 size={16} strokeWidth={1.5} />
              </button>
              )}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3 mt-4 pt-4 border-t border-[#E4E7E1]">
          <ColorSwatch value={newCatColor} onChange={setNewCatColor} testId="category-new-color" />
          <input
            data-testid="category-new-name"
            value={newCatName}
            onChange={(e) => setNewCatName(e.target.value)}
            placeholder="Nuova categoria"
            className="flex-1 min-w-0 px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm focus:outline-none focus:border-[#A3B897]"
          />
          <button
            type="button"
            onClick={() => {
              if (!newCatName.trim()) return;
              addCategory(newCatName, newCatColor);
              setNewCatName("");
              toast.success("Categoria aggiunta");
            }}
            className="tl-btn-primary inline-flex items-center gap-1.5 px-4 py-2 bg-[#A3B897] hover:bg-[#8FA087] text-white rounded-full text-sm transition-all"
            data-testid="category-add"
          >
            <Plus size={14} strokeWidth={1.75} /> Aggiungi
          </button>
        </div>
        <CategoryTrash />
      </section>

      {/* Quotes */}
      <section className="tl-paper p-6">
        <h2 className="font-serif-display text-2xl text-[#3B443D] mb-1">Frasi motivazionali</h2>
        <p className="text-sm text-[#6A7368] mb-3">Una frase per riga. Ne compare una sotto la data, nelle pagine Giorno e Settimana.</p>
        <textarea
          data-testid="settings-quotes"
          rows={10}
          value={quotesText}
          onChange={(e) => setQuotesText(e.target.value)}
          className="w-full px-3 py-2 bg-white border border-[#E4E7E1] rounded-lg text-sm focus:outline-none focus:border-[#A3B897] font-mono"
        />
        <div className="flex justify-end mt-3">
          <button onClick={saveQuotes} className="tl-btn-primary px-5 py-2 bg-[#A3B897] hover:bg-[#8FA087] text-white rounded-full text-sm transition-all" data-testid="settings-quotes-save">
            Salva frasi
          </button>
        </div>
      </section>

      {/* Data */}
      <section className="tl-paper p-6">
        <h2 className="font-serif-display text-2xl text-[#3B443D] mb-3">Dati</h2>
        <div className="flex flex-wrap gap-2">
          <button
            className="tl-btn-soft px-4 py-2 rounded-full bg-white border border-[#E4E7E1] text-sm text-[#58635B] hover:bg-[#F3F5F0]"
            data-testid="data-export"
            onClick={() => {
              const blob = new Blob([JSON.stringify(store, null, 2)], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `tempo-leggero-backup-${new Date().toISOString().slice(0, 10)}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
          >
            Esporta backup
          </button>
          <button
            className="tl-btn-soft px-4 py-2 rounded-full bg-white border border-[#E4E7E1] text-sm text-[#C48B97] hover:bg-[#F3F5F0]"
            data-testid="data-clear"
            onClick={() => {
              if (window.confirm("Sei sicura di voler cancellare tutti i dati?")) {
                localStorage.removeItem("tempo-leggero:v1");
                window.location.reload();
              }
            }}
          >
            Cancella tutti i dati
          </button>
        </div>
      </section>
    </div>
  );
}

function ColorSwatch({ value, onChange, testId }) {
  return (
    <label className="relative inline-block flex-shrink-0 w-9 h-9 rounded-md cursor-pointer border border-[#E4E7E1]" style={{ background: value }}>
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="opacity-0 absolute inset-0 w-full h-full cursor-pointer"
        data-testid={testId}
      />
    </label>
  );
}
